import { log as logConfig } from 'constants/config';
import { DEFAULT_FALLBACK_LOCATION } from 'constants/geoBlocking';
import { trackEvent } from 'services/client/analytics';
import {
  GOOGLE_MAPS_API_AUTOCOMPLETE,
  GOOGLE_MAPS_API_GEOCODING,
  GOOGLE_MAPS_API_GEOLOCATION,
  GOOGLE_MAPS_API_PLACE_DETAILS,
} from 'services/client/analytics/events';

// Re-export for convenience in GeoLocationContext
export { DEFAULT_FALLBACK_LOCATION };

const MINUTE = 60 * 1000;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;
const SHORT_DEBOUNCE_TIME = 400;
const LONG_DEBOUNCE_TIME = 800;

const SHOULD_LOG = true;
const SHOULD_LOG_PAID_REQUESTS = true; // Separate control for paid API request logs

export const log = (...args: any[]) => {
  if (SHOULD_LOG) logConfig(...args);
};

export const logPaidRequest = (apiName: string, details: any) => {
  if (SHOULD_LOG_PAID_REQUESTS) {
    console.warn(`💰💰💰 PAID API REQUEST - ${apiName} 💰💰💰`, {
      ...details,
      timestamp: new Date().toISOString(),
      warning: 'This request will be billed to your Google Cloud account',
    });
  }

  // Track in PostHog
  const eventData = {
    api_name: apiName,
    page_url: typeof window !== 'undefined' ? window.location.href : 'unknown',
    page_path: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    page_route_pattern: details.routePattern || 'unknown',
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    ...details,
    timestamp: new Date().toISOString(),
  };

  // Map API names to specific PostHog events
  let eventName = '';
  if (apiName.toLowerCase().includes('geocoding')) {
    eventName = GOOGLE_MAPS_API_GEOCODING;
  } else if (apiName.toLowerCase().includes('geolocation')) {
    eventName = GOOGLE_MAPS_API_GEOLOCATION;
  } else if (apiName.toLowerCase().includes('autocomplete')) {
    eventName = GOOGLE_MAPS_API_AUTOCOMPLETE;
  } else if (apiName.toLowerCase().includes('place details')) {
    eventName = GOOGLE_MAPS_API_PLACE_DETAILS;
  }

  if (eventName) {
    trackEvent(eventName, eventData);
  }
};

/**
 * Feature flags for toggling optimizations
 * Set to false to use original behavior
 */
export const GEO_FEATURE_FLAGS = {
  // Caching features
  ENABLE_AUTOCOMPLETE_CACHE: true,
  ENABLE_GEOCODING_CACHE: true,
  ENABLE_PLACE_DETAILS_CACHE: true,

  // Bot protection
  ENABLE_BOT_DETECTION: true, // TEMPORARILY DISABLED FOR TESTING INCOGNITO
  ENABLE_RATE_LIMITING: true,

  // Performance optimizations
  ENABLE_REQUEST_DEDUPLICATION: true,
  INCREASED_DEBOUNCE_TIME: true, // 800ms instead of 400ms
  MINIMUM_AUTOCOMPLETE_LENGTH: true, // 3 characters minimum

  // Context/storage features
  USE_LOCATION_CONTEXT: true,
  PERSIST_TO_BROWSER_STORAGE: true,
  USE_USER_PROFILE_GEOMETRY: true,

  // Google Maps loading
  BLOCK_MAPS_FOR_BOTS: true,

  // Location estimation
  USE_IP_GEOLOCATION: false, // Set to true to use free IP-based location instead of Google

  // User profile location
  USE_USER_PROFILE_LOCATION: true, // Use location from user profile if available

  // URL query parameter support
  USE_URL_COORDINATES: true,
  PERSIST_COORDINATES_TO_URL: true,

  // Debug mode
  DEBUG_MODE: process.env.NODE_ENV === 'development',

  // Geo-blocking for specific countries (CN, SG)
  // Uses IP-based detection which is harder to spoof than timezone/locale
  ENABLE_GEO_BLOCKING: true,

  // Soft block mode: Allow blocked users to search manually (handles false positives)
  // When true: Blocked users can still use autocomplete/map after searching
  // When false: Complete lockdown (no Google Maps loaded, no API calls)
  ENABLE_SOFT_BLOCK_MODE: true,
};

// Autocomplete settings
export const AUTOCOMPLETE_CONFIG = {
  DEFAULT_DEBOUNCE: GEO_FEATURE_FLAGS.INCREASED_DEBOUNCE_TIME
    ? LONG_DEBOUNCE_TIME
    : SHORT_DEBOUNCE_TIME,
  MIN_LENGTH: GEO_FEATURE_FLAGS.MINIMUM_AUTOCOMPLETE_LENGTH ? 3 : 0,
  CACHE_TTL: MINUTE * 30,
};

export const CACHE_CONFIG = {
  GEOCODING_TTL: DAY * 7,
  PLACE_DETAILS_TTL: HOUR * 2,
  LOCATION_TTL: MINUTE * 90,
};
